class CartItems extends HTMLElement {
  connectedCallback() {
    this.unsubscribeCartChange = subscribe('cart:change', ({ line, quantity }) =>
      this.cartChange(line, quantity)
    );
    this.unsubscribeRefreshCart = subscribe('cart:added', () => this.refreshCart());
    // 'cart:refresh': Re-Render ohne Drawer-Oeffnung (z.B. Undo nach "Artikel entfernt",
    // Logik in line-item.js). 'cart:added' wuerde den Drawer aufklappen.
    this.unsubscribeCartRefresh = subscribe('cart:refresh', () => this.refreshCart());
    this.unsubscribeCartUpdated = subscribe('cart:updated', () => this.enforceMoq());
    this.enforcingMoq = false;
    this.enforceMoq();
  }

  disconnectedCallback() {
    this.unsubscribeCartChange();
    this.unsubscribeRefreshCart();
    this.unsubscribeCartRefresh();
    this.unsubscribeCartUpdated();
  }

  /**
   * Change the cart.
   * @description Use the /{locale}/cart/change.js endpoint to change the quantity, properties, and selling_plan properties of a cart line item. Only items already in your cart can be changed, and you can change only one line item at a time.
   * @tutorial https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
   */
  async cartChange(line, quantity) {
    publish('cart:updating', {
      line,
    });
    const body = JSON.stringify({
      line,
      quantity,
      sections: 'fetch-line-items',
      sections_url: window.location.pathname,
    });

    try {
      const { error, data } = await this.fetchCart(routes.cart_change_url, body);

      if (error) throw new Error(error);

      this.renderSections(data.sections);

      publish('cart:updated', {
        itemCount: data.item_count,
        data,
      });
    } catch (error) {
      console.error(error);
      window.toast.error(error.message);

      this.enforcingMoq = false;

      publish('cart:error', {
        line,
      });
    }
  }

  /**
   * Fetch the cart.
   * @param {string} input - The URL to fetch.
   * @param {string} body - The request body.
   * @returns {Promise} The response.
   *
   * @example
   * const json = await this.fetchCart(routes.cart_change_url, body);
   */
  async fetchCart(input, body) {
    const response = await fetch(input, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body,
    });

    const data = await response.json();

    if (!response.ok) return { error: data.errors, data: null };

    return { error: null, data };
  }

  /**
   * Refresh the cart.
   */
  async refreshCart() {
    try {
      const response = await fetch(`${Shopify.routes.root}?sections=fetch-line-items`);
      const data = await response.json();

      const sections = {};
      sections['fetch-line-items'] = data['fetch-line-items'];

      publish('cart:updated', {
        itemCount: data.item_count,
      });

      this.renderSections(sections);
    } catch (error) {
      window.toast.error(error.message);
    }
  }

  /**
   * Render the sections.
   * @param {Object} json - The JSON response.
   * @example
   * const json = await this.fetchCart(routes.cart_change_url, body);
   * this.renderSections(json);
   */
  renderSections(sections) {
    const html = new DOMParser().parseFromString(sections['fetch-line-items'], 'text/html');
    this.innerHTML = html.querySelector('line-items').innerHTML;

    // Inline-Undo ("Artikel entfernt | Rueckgaengig") an der Stelle der Liste einsetzen,
    // NACH dem innerHTML-Re-Render, sonst wuerde die Zeile sofort ueberschrieben.
    // Logik in line-item.js (tsiRenderUndoRow); tut nichts, wenn kein Undo anhaengig ist.
    if (window.tsiRenderUndoRow) window.tsiRenderUndoRow(this);

    this.enforcingMoq = false;
    this.enforceMoq();
  }

  enforceMoq() {
    if (this.enforcingMoq) return;

    // Nur echte Warenkorbpositionen pruefen: Die versteckten Zubehoer-Stepper aus
    // cart-drawer-recommendations liegen ausserhalb von <line-item> und tragen durch
    // "line_item.index | plus: 1" bei leerem line_item faelschlich data-item-index="1" -
    // sie duerfen hier niemals eine Warenkorbposition aendern.
    const inputs = this.querySelectorAll('line-item quantity-input');

    inputs.forEach((quantityInput) => {
      // Wert aus dem inneren Input-Feld lesen. Vorher wurde .value auf dem Custom
      // Element gelesen (immer undefined, "undefined < moq" ist immer false), wodurch
      // die MOQ-Korrektur nie gegriffen hat.
      const field = quantityInput.querySelector('input');
      if (!field) return;

      const moq = parseInt(quantityInput.dataset.moq);
      const line = parseInt(quantityInput.dataset.itemIndex);
      const value = parseInt(field.value);

      if (isNaN(moq) || isNaN(line) || isNaN(value)) return;

      if (value > 0 && value < moq) {
        this.enforcingMoq = true;
        this.cartChange(line, moq);
      }
    });
  }
}

customElements.define('line-items', CartItems);